import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ScaleIcon, DownloadIcon } from "lucide-react";
import { toast } from "sonner";
import { FINALIDADES } from "@/components/EtapaOperacao";
import { fmtKg } from "./IndicadorBalanca";

const inputCls = "bg-[#0A0A0C] border-border rounded-none h-10 text-sm";

/** Rótulos das pesagens conforme o tipo de operação. */
export const rotulos = (tipo) => (
  tipo === "CARGA" ? { p1: "1ª Pesagem — TARA (entrada vazia)", p2: "2ª Pesagem — BRUTO (saída carregada)" }
  : tipo === "DESCARGA" ? { p1: "1ª Pesagem — BRUTO (entrada carregada)", p2: "2ª Pesagem — TARA (saída vazia)" }
  : { p1: "1ª Pesagem — peso na ENTRADA", p2: "2ª Pesagem — peso na SAÍDA" }
);

/**
 * Modal de pesagem. modo=1 → 1ª pesagem de uma operação liberada pela Portaria (prop `op`);
 * modo=2 → 2ª pesagem de um ticket ABERTO (prop `ticket`).
 */
export function PesagemModal({ open, onOpenChange, modo, op, ticket, serial, onSaved }) {
  const [peso, setPeso] = useState("");
  const [origem, setOrigem] = useState("MANUAL");
  const [nf, setNf] = useState("");
  const [produto, setProduto] = useState("");
  const [obs, setObs] = useState("");
  const [saving, setSaving] = useState(false);
  const alvo = modo === 2 ? ticket : op;
  const tipo = alvo?.tipo_operacao;
  const r = rotulos(tipo);

  useEffect(() => {
    if (!open) return;
    setPeso(""); setOrigem("MANUAL");
    setNf(alvo?.nota_fiscal || ""); setProduto(alvo?.produto || ""); setObs(alvo?.observacoes || "");
  }, [open, alvo?.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  const capturar = () => {
    if (serial.peso == null) return toast.error("Sem leitura do indicador — conecte a porta ou digite o peso");
    if (!serial.estavel) toast.warning("Leitura ainda não está estável — confira o valor capturado");
    setPeso(String(Math.round(serial.peso))); setOrigem("SERIAL");
  };

  const salvar = async () => {
    const kg = parseFloat(String(peso).replace(",", "."));
    if (!kg || kg <= 0) return toast.error("Informe o peso em kg");
    setSaving(true);
    try {
      const body = { peso: kg, origem, nota_fiscal: nf || null, produto: produto || null, observacoes: obs || null };
      const { data } = modo === 2
        ? await api.post(`/balanca/pesagens/${ticket.id}/segunda`, body)
        : await api.post("/balanca/pesagens", { ...body, operacao_id: op.id });
      toast.success(modo === 2 ? `Ticket ${data.ticket_str} finalizado · líquido ${fmtKg(data.peso_liquido)}` : `Ticket ${data.ticket_str} aberto · ${fmtKg(data.peso1)}`);
      onOpenChange(false);
      onSaved?.(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const liquidoPrev = modo === 2 && peso ? Math.abs(parseFloat(String(peso).replace(",", ".")) - ticket.peso1) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#14151A] border-border rounded-none max-w-2xl" data-testid="modal-pesagem">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-wider flex items-center gap-2">
            <ScaleIcon className="w-5 h-5 text-[#FFD60A]" /> {modo === 2 ? `2ª Pesagem · Ticket ${ticket?.ticket_str}` : "1ª Pesagem · Novo ticket"}
          </DialogTitle>
        </DialogHeader>
        {alvo && (
          <div className="space-y-4">
            <div className="border border-border bg-[#0A0A0C] p-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs" data-testid="pesagem-dados-veiculo">
              <div><div className="text-[9px] uppercase text-muted-foreground">Placas</div>
                <div className="font-mono font-bold text-[#FFD60A]">{alvo.placa_cavalo || "—"}{alvo.placa_carreta ? ` / ${alvo.placa_carreta}` : ""}</div></div>
              <div><div className="text-[9px] uppercase text-muted-foreground">Frota</div><div className="font-mono font-bold text-[#00E5FF]">{alvo.frota || "—"}</div></div>
              <div><div className="text-[9px] uppercase text-muted-foreground">Motorista</div><div className="font-semibold text-white truncate">{alvo.nome || "—"}</div></div>
              <div><div className="text-[9px] uppercase text-muted-foreground">Empresa</div><div className="truncate">{alvo.empresa || "—"}</div></div>
              <div className="col-span-2 md:col-span-4 flex gap-1 flex-wrap items-center">
                <span className="text-[9px] uppercase text-muted-foreground mr-1">Operação</span>
                <span className={`text-[10px] font-bold px-1.5 border ${tipo === "CARGA" ? "text-[#34C759] border-[#34C759]/50" : tipo === "DESCARGA" ? "text-[#00E5FF] border-[#00E5FF]/50" : "text-[#FFD60A] border-[#FFD60A]/50"}`}>{tipo}</span>
                {alvo.avulsa && <span className="text-[10px] font-bold px-1.5 border text-[#FF9F0A] border-[#FF9F0A]/60" data-testid="pesagem-tag-avulsa">AVULSA · sem entrada na Portaria</span>}
                {(alvo.finalidades || []).map((f) => <span key={f} className="text-[10px] text-muted-foreground">· {FINALIDADES[f]}</span>)}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="border border-border p-3 bg-[#0A0A0C]/60">
                <div className="text-[9px] uppercase tracking-wider text-muted-foreground">{r.p1}</div>
                {modo === 2
                  ? <div className="font-mono text-2xl font-black text-[#FFD60A] mt-1">{fmtKg(ticket.peso1)} <span className="text-[9px] text-muted-foreground font-sans">({ticket.peso1_origem})</span></div>
                  : <PesoInput peso={peso} setPeso={(v) => { setPeso(v); setOrigem("MANUAL"); }} origem={origem} serial={serial} capturar={capturar} testid="pesagem-peso" />}
              </div>
              <div className={`border p-3 ${modo === 2 ? "border-[#34C759]/50 bg-[#34C759]/5" : "border-border opacity-50"}`}>
                <div className="text-[9px] uppercase tracking-wider text-muted-foreground">{r.p2}</div>
                {modo === 2
                  ? <PesoInput peso={peso} setPeso={(v) => { setPeso(v); setOrigem("MANUAL"); }} origem={origem} serial={serial} capturar={capturar} testid="pesagem-peso" />
                  : <div className="font-mono text-2xl font-black text-muted-foreground mt-1">— kg</div>}
              </div>
            </div>
            {liquidoPrev != null && Number.isFinite(liquidoPrev) && (
              <div className="border border-[#34C759]/50 bg-[#34C759]/10 px-3 py-2 flex items-center justify-between" data-testid="pesagem-liquido-preview">
                <span className="text-xs text-[#34C759] font-semibold uppercase tracking-wider">Peso líquido (|2ª − 1ª|)</span>
                <span className="font-mono text-xl font-black text-[#34C759]">{fmtKg(liquidoPrev)}</span>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Nota Fiscal</Label>
                <Input value={nf} onChange={(e) => setNf(e.target.value.toUpperCase())} className={`${inputCls} font-mono`} placeholder="Nº da NF" data-testid="pesagem-nf" /></div>
              <div><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Produto</Label>
                <Input value={produto} onChange={(e) => setProduto(e.target.value.toUpperCase())} className={inputCls} placeholder="Produto transportado" data-testid="pesagem-produto" /></div>
            </div>
            <div><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Observações</Label>
              <Textarea value={obs} onChange={(e) => setObs(e.target.value)} rows={2} className="bg-[#0A0A0C] border-border rounded-none text-sm" placeholder="Lacre, avarias, umidade..." data-testid="pesagem-obs" /></div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-none">Cancelar</Button>
          <Button onClick={salvar} disabled={saving} data-testid="pesagem-salvar"
                  className={`rounded-none font-bold uppercase tracking-wider text-black ${modo === 2 ? "bg-[#34C759] hover:bg-[#34C759]/85" : "bg-[#FFD60A] hover:bg-[#FFD60A]/85"}`}>
            {saving ? "Salvando..." : modo === 2 ? "Finalizar ticket" : "Salvar 1ª pesagem"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function PesoInput({ peso, setPeso, origem, serial, capturar, testid }) {
  const conectado = serial.estado === "CONECTADO";
  return (
    <div className="mt-1 space-y-2">
      <div className="flex gap-2">
        <Input value={peso} onChange={(e) => setPeso(e.target.value.replace(/[^\d.,]/g, ""))} inputMode="numeric" placeholder="0"
               className="bg-[#0A0A0C] border-border rounded-none h-12 font-mono text-2xl font-black text-[#FFD60A] tabular-nums" data-testid={testid} />
        <Button type="button" onClick={capturar} disabled={serial.peso == null} data-testid={`${testid}-capturar`}
                className={`rounded-none h-12 px-3 font-bold uppercase tracking-wider text-[10px] ${serial.estavel ? "bg-[#34C759] text-black hover:bg-[#34C759]/85" : "bg-[#FF9F0A] text-black hover:bg-[#FF9F0A]/85"}`}>
          <DownloadIcon className="w-4 h-4 mr-1" /> Capturar<br />{serial.peso != null ? fmtKg(serial.peso) : "—"}
        </Button>
      </div>
      <div className="text-[10px] text-muted-foreground flex items-center justify-between">
        <span>Origem: <b className={origem === "SERIAL" ? "text-[#34C759]" : "text-[#FF9F0A]"} data-testid={`${testid}-origem`}>{origem === "SERIAL" ? "INDICADOR (serial)" : "DIGITADO (manual)"}</b></span>
        <span>{conectado ? (serial.estavel ? "leitura estável" : "aguardando estabilizar") : "indicador desconectado"}</span>
      </div>
    </div>
  );
}
